import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { contactInput } from "@shared/schemas/contacts";
import { errorMessage } from "@web/lib/api";
import { validateContact } from "@shared/validation/contacts";
import { evaluation, parseInput } from "@shared/validation/issues";
import { useFormValidation } from "@web/lib/formValidation";
import { useBatchValidation } from "@web/lib/validation";
import { fmtPhone, fullName } from "@web/lib/format";
import { api, contactsQuery, type Contact } from "@web/lib/queries";
import { Avatar, Button, Inline, Row, RowGroup, SearchBar, Skeleton, Stack, Text, TextField, ValidationBadge, ValidationSummary } from "@web/ui";

export type ContactPickerProps = { value: Contact | null; onChange: (c: Contact | null) => void | null | Promise<void>; /** Synchronous fence for enclosing form saves/close. */ onPendingChange?: (pending: boolean) => void; /** Hide the "Change" affordance. */ locked?: boolean };

const EMPTY = { firstName: "", lastName: "", companyName: "", title: "", phone: "", email: "" };

/** Inline contact chooser: search existing (name / phone / email) or add a new one (dedupes server-side). */
export function ContactPicker({ value, onChange, onPendingChange, locked }: ContactPickerProps) {
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [createdChoice, setCreatedChoice] = useState<Contact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const results = useQuery(contactsQuery(q));
  const input = { ...form, email: form.email || null, phone: form.phone || null };
  const parsedInput = parseInput(contactInput, input, { type: "contact" }, "contact.save");
  const validation = useFormValidation(evaluation(adding && !createdChoice ? [...parsedInput.issues, ...validateContact(input).issues] : [], false));
  const { busy } = validation;
  const checks = useBatchValidation(!value && !adding ? (results.data ?? []).map(contact => ({ entityType: "contact", id: contact.id })) : []);
  const edit = (key: keyof typeof form, value: string) => validation.edit(() => { if (createdChoice) return; setError(null); setForm(previous => ({ ...previous, [key]: value })); });
  const field = (name: string) => ({ ...validation.field(name), disabled: busy || Boolean(createdChoice) });

  if (value) {
    return (
      <RowGroup>
        <Row
          icon={<Avatar name={fullName(value) || "?"} size="sm" />}
          title={fullName(value) || value.email || value.phone || "Contact"}
          subtitle={[value.companyName, value.title, fmtPhone(value.phone), value.email].filter(Boolean).join(" · ")} wrap
          trailing={
            locked ? undefined : (
              <Button variant="secondary" fullWidth={false} disabled={busy} onClick={() => void select(null)}>
                Change
              </Button>
            )
          }
        />
      </RowGroup>
    );
  }

  async function select(chosen?: Contact | null) {
    const parsed = parsedInput;
    if (chosen === undefined && !createdChoice) {
      if (!parsed.success) { validation.report(parsed.issues); return; }
      if (!form.firstName && !form.lastName && !form.phone && !form.email) return setError("Enter a name, phone or email");
    }
    if (!validation.begin("contact.save")) return;
    onPendingChange?.(true);
    setError(null);
    try {
      let c = chosen === undefined ? createdChoice : chosen;
      if (chosen === undefined && !c) {
        if (!parsed.success) return;
        c = await api.createContact(parsed.data);
        // A failed parent attachment must retry this saved contact, never a
        // second creation or an silently edited copy of its original draft.
        setCreatedChoice(c);
      }
      await onChange(c);
      setCreatedChoice(null);
      setForm(EMPTY);
      setAdding(false);
    } catch (e) {
      validation.reject(e);
      setError(errorMessage(e));
    } finally {
      onPendingChange?.(false);
      validation.finish();
    }
  }

  if (adding) {
    return (
      <Stack gap={3}>
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        {createdChoice && <Text variant="callout">Contact saved. Retry attaching this contact; another contact will not be created.</Text>}
        <Inline gap={2}>
          <TextField label="First name" className="flex-1" autoComplete="off" value={form.firstName} {...field("firstName")} onChange={(e) => edit("firstName", e.target.value)} />
          <TextField label="Last name" className="flex-1" autoComplete="off" value={form.lastName} {...field("lastName")} onChange={(e) => edit("lastName", e.target.value)} />
        </Inline>
        <TextField label="Company" value={form.companyName} {...field("companyName")} onChange={(e) => edit("companyName", e.target.value)} />
        <TextField label="Job title" value={form.title} {...field("title")} onChange={(e) => edit("title", e.target.value)} />
        <TextField label="Phone" type="tel" inputMode="tel" autoComplete="off" value={form.phone} {...field("phone")} onChange={(e) => edit("phone", e.target.value)} />
        <TextField label="Email" type="email" inputMode="email" autoComplete="off" value={form.email} {...field("email")} onChange={(e) => edit("email", e.target.value)} />
        {error && <Text tone="danger">{error}</Text>}
        <Inline gap={2}>
          <Button variant="secondary" disabled={busy} onClick={() => validation.edit(() => { setAdding(false); setError(null); if (createdChoice) { setCreatedChoice(null); setForm(EMPTY); } })}>
            Back to search
          </Button>
          <Button loading={busy} onClick={() => void select()}>
            {createdChoice ? "Retry attachment" : "Add contact"}
          </Button>
        </Inline>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      {(error || busy) && <ValidationSummary result={validation.result} local loading={busy} unavailable={validation.unavailable} />}
      {error && <Text tone="danger">{error}</Text>}
      <SearchBar value={q} onChange={value => validation.edit(() => setQ(value))} placeholder="Search name, phone or email" label="Search contacts" />
      {results.isError && <Stack gap={2}><Text tone="danger">Contact search is unavailable.</Text><Button variant="secondary" onClick={() => void results.refetch()}>Retry search</Button></Stack>}
      {(checks.isError || checks.data?.checksComplete === false) && <ValidationSummary result={checks.data} unavailable onRetry={() => void checks.refetch()} />}
      {results.isLoading && <Skeleton count={2} />}
      {results.data && results.data.length > 0 && (
        <RowGroup>
          {results.data.map((c) => (
              <Row key={c.id} icon={<Avatar name={fullName(c) || "?"} size="sm" />} title={fullName(c) || c.email || c.phone || "Contact"} subtitle={[c.companyName, c.title, fmtPhone(c.phone), c.email].filter(Boolean).join(" · ")} wrap trailing={<ValidationBadge result={checks.data?.records.find(record => record.entity.type === "contact" && record.entity.id === c.id)} unavailable={checks.isError} />} onClick={busy ? undefined : () => void select(c)} />
          ))}
        </RowGroup>
      )}
      {results.data && results.data.length === 0 && (
        <Text variant="callout" tone="secondary">
          No matches.
        </Text>
      )}
      <Button variant="secondary" disabled={busy} icon={<UserPlus aria-hidden size={18} />} onClick={() => validation.edit(() => setAdding(true))}>
        New contact
      </Button>
    </Stack>
  );
}
